import Pusher from 'pusher-js'
import { toast } from 'react-toastify'

class ChatWebSocketService {
  constructor() {
    this.pusher = null;
    this.channels = new Map();
    this.isConnected = false;
    this.listeners = new Map();
    this.notificationSound = null;
    this.lastSoundMessageId = null;
    this.soundCooldown = null;
    this.currentUserId = null;
    this.tabVisibilityChangeListeners = [];

    // Sound settings - can be configured by user
    this.soundSettings = {
      enabled: true,
      playWhenTabActive: false, // Default: only play when tab is inactive
      volume: 0.3,
      cooldownMs: 1000, // Prevent duplicate sounds
    };

    this.initializeNotificationSound();
    this.setupTabVisibilityTracking();
  }

  // Initialize notification sound
  initializeNotificationSound() {
    try {
      // Use R2 URL for reliable audio delivery
      const audioUrl = 'https://adnexiohr-assets.adnexio.jobs/notification.mp3';

      this.notificationSound = new Audio(audioUrl);
      this.notificationSound.preload = 'auto';
      this.notificationSound.volume = this.soundSettings.volume;

      // Add error handling for audio loading
      this.notificationSound.addEventListener('error', (e) => {
        console.error('❌ Failed to load notification sound from R2:', {
          src: this.notificationSound.src,
          error: e,
          networkState: this.notificationSound.networkState,
          readyState: this.notificationSound.readyState
        });
      });

      this.notificationSound.addEventListener('canplaythrough', () => {

      });

      this.notificationSound.addEventListener('loadstart', () => {

      });
    } catch (error) {
      console.warn('Could not initialize notification sound:', error)
    }
  }

  // Connect to Pusher/Soketi
  connect(pusherConfig, token, userId) {
    if (this.pusher && this.isConnected) {

      return
    }

    try {
      // Initialize Pusher client
      this.pusher = new Pusher(pusherConfig.key, {
        cluster: pusherConfig.cluster || 'mt1', // Required by Pusher-js
        host: pusherConfig.host,
        port: pusherConfig.port,
        useTLS: pusherConfig.useTLS,
        encrypted: pusherConfig.encrypted, // Use config value
        authEndpoint: `${pusherConfig.authEndpoint}/broadcasting/auth`,
        auth: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
        wsHost: pusherConfig.host, // Explicitly set WebSocket host
        wsPort: pusherConfig.port, // Explicitly set WebSocket port
        forceTLS: false, // Disable forced TLS
        enableStats: false, // Disable stats for local development
        enabledTransports: ['ws', 'wss'], // Only use WebSocket transport
      })

      // Store current user ID for later use
      this.setCurrentUserId(userId)

      this.setupEventListeners()
    } catch (error) {
      console.error('Failed to connect to Pusher:', error)
      this.handleConnectionError(error)
    }
  }

  // Setup Pusher event listeners
  setupEventListeners() {
    if (!this.pusher) return

    // Connection events
    this.pusher.connection.bind('connected', () => {
      this.isConnected = true
      this.emit('connected', { clientId: this.pusher.connection.socket_id })

      // Setup global channel after connection is established
      this.setupGlobalChannel()
    })

    this.pusher.connection.bind('disconnected', () => {
      this.isConnected = false
    })

    this.pusher.connection.bind('error', (error) => {
      console.error('Pusher connection error:', error)
      this.handleConnectionError(error)
    })
  }

  // Setup global channel for all events
  setupGlobalChannel() {
    if (!this.pusher || !this.isConnected) return

    try {
      const globalChannel = this.pusher.subscribe('global')
      globalChannel.bind_global((eventName, data) => {

        // Handle specific events
        if (eventName === 'newMessage') {
          this.handleNewMessage(data)
        }

        // Also emit all events to our local listeners for debugging
        if (eventName !== 'pusher:subscription_succeeded') {
          this.emit(eventName, data)
        }
      })
    } catch (error) {
      console.error('Failed to setup global channel:', error)
    }
  }

  // Setup reconnection logic
  setupReconnection() {
    if (!this.socket) return

    this.socket.on('reconnect', (attemptNumber) => {
      this.isConnected = true
      this.reconnectAttempts = 0
    })

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      this.reconnectAttempts = attemptNumber
    })

    this.socket.on('reconnect_failed', () => {
      console.error('Failed to reconnect to WebSocket server')
      this.isConnected = false
      toast.error('Lost connection to chat server. Please refresh the page.')
    })
  }

  // Handle new message
  handleNewMessage(data) {
    const { message } = data

    // Debug: Log current user ID and message sender
    const currentUserId = this.getCurrentUserId()

    // Check if message is from current user using multiple ID fields
    const isFromCurrentUser =
      message.sender_uuid === currentUserId ||
      message.sender_id === currentUserId ||
      message.user_id === currentUserId

    // Check if browser tab is active/inactive
    const isTabActive = this.isTabActive()

    // Only play notification sound and show toast for messages from other users
    if (!isFromCurrentUser) {
      const messageId = message.uuid || message.id || message.message_id

      // Prevent duplicate sounds for the same message within 1 second
      if (messageId && this.lastSoundMessageId === messageId) {
      } else {
        // Check if notification sound should be played
        if (this.shouldPlayNotificationSound(isFromCurrentUser)) {

          this.playNotificationSound()
        } else {

        }

        // Set cooldown to prevent duplicate sounds
        this.lastSoundMessageId = messageId
        if (this.soundCooldown) clearTimeout(this.soundCooldown)
        this.soundCooldown = setTimeout(() => {
          this.lastSoundMessageId = null;
        }, this.soundSettings.cooldownMs);

        // Show toast notification (always show regardless of tab status)
        const messagePreview = message.content
          ? message.content.length > 50
            ? message.content.substring(0, 50) + '...'
            : message.content
          : 'New message'
        toast.info(`New message from ${message.sender_name || 'Unknown'}: ${messagePreview}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
      }
    } else {

    }

    // Emit to listeners
    this.emit('newMessage', data)
  }

  // Handle notifications
  handleNotification(data) {
    // Check if notification is from current user
    const isFromCurrentUser = data.sender_uuid === this.getCurrentUserId()

    // Check if browser tab is active/inactive
    const isTabActive = this.isTabActive()


    // Check if notification sound should be played
    if (this.shouldPlayNotificationSound(isFromCurrentUser)) {

      this.playNotificationSound()
    } else {

    }

    // Show toast notification (always show regardless of tab status)
    toast.info(data.message || 'New notification', {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    })

    // Emit to listeners
    this.emit('notification', data)
  }

  // Play notification sound
  playNotificationSound() {
    try {
      if (this.notificationSound && this.soundSettings.enabled) {

        this.notificationSound.volume = this.soundSettings.volume;
        this.notificationSound.currentTime = 0;

        const playPromise = this.notificationSound.play()

        if (playPromise !== undefined) {
          playPromise
            .then(() => {

            })
            .catch((error) => {
              console.warn('Could not play notification sound:', error)

              // Handle autoplay policy restrictions
              if (error.name === 'NotAllowedError') {
                console.warn(
                  'Autoplay blocked by browser. User interaction required to enable sounds.'
                )
                // Try to enable audio context on next user interaction
                this.enableAudioOnUserInteraction()
              } else if (error.name === 'NotSupportedError') {
                console.error('Audio format not supported by browser')
              } else {
                console.error('Unknown audio error:', error)
              }
            })
        }
      } else if (!this.soundSettings.enabled) {

      } else {
        console.warn('Notification sound not initialized')
      }
    } catch (error) {
      console.warn('Error playing notification sound:', error)
    }
  }

  // Enable audio on user interaction (for autoplay policy)
  enableAudioOnUserInteraction() {

    const enableAudio = () => {
      if (this.notificationSound) {
        this.notificationSound
          .play()
          .then(() => {

          })
          .catch((error) => {
            console.warn('Still cannot play audio after user interaction:', error)
          })
      }
      // Remove listeners after first interaction
      document.removeEventListener('click', enableAudio)
      document.removeEventListener('keydown', enableAudio)
      document.removeEventListener('touchstart', enableAudio)
    }

    // Add listeners for user interaction
    document.addEventListener('click', enableAudio, { once: true })
    document.addEventListener('keydown', enableAudio, { once: true })
    document.addEventListener('touchstart', enableAudio, { once: true })
  }

  // Join conversation
  joinConversation(conversationId, userId) {
    if (!this.pusher || !this.isConnected) {
      console.warn('Pusher not connected')
      return
    }

    try {
      // Subscribe to conversation channel
      const channelName = `conversation.${conversationId}`
      const channel = this.pusher.subscribe(channelName)
      this.channels.set(channelName, channel)

      // Bind to conversation events
      channel.bind('newMessage', (data) => {
        this.handleNewMessage(data)
      })

      // Also bind to the event that the Chat API broadcasts
      channel.bind('message', (data) => {
        this.handleNewMessage(data)
      })

      // Debug: bind to all events to see what's coming through
      channel.bind_global((eventName, data) => {

        // Handle specific events
        if (eventName === 'newMessage') {
          this.handleNewMessage(data)
        }

        // Also emit all events to our local listeners for debugging
        if (eventName !== 'pusher:subscription_succeeded') {
          this.emit(eventName, data)
        }
      })

      channel.bind('messageUpdated', (data) => {
        this.emit('messageUpdated', data)
      })

      channel.bind('messageDeleted', (data) => {
        this.emit('messageDeleted', data)
      })

      channel.bind('conversationUpdated', (data) => {
        this.emit('conversationUpdated', data)
      })

      channel.bind('userTyping', (data) => {
        this.emit('userTyping', data)
      })
    } catch (error) {
      console.error(`Failed to join conversation ${conversationId}:`, error)
    }
  }

  // Leave conversation
  leaveConversation(conversationId, userId) {
    if (!this.pusher || !this.isConnected) {
      console.warn('Pusher not connected')
      return
    }

    try {
      // Unsubscribe from conversation channel
      const channelName = `conversation.${conversationId}`
      const channel = this.channels.get(channelName)
      if (channel) {
        this.pusher.unsubscribe(channelName)
        this.channels.delete(channelName)
      }
    } catch (error) {
      console.error(`Failed to leave conversation ${conversationId}:`, error)
    }
  }

  // Send typing indicator
  sendTypingIndicator(conversationId, userId, isTyping) {
    if (!this.pusher || !this.isConnected) {
      return
    }
  }

  // Broadcast message to conversation channel
  async broadcastMessage(conversationId, message) {
    if (!this.pusher || !this.isConnected) {
      console.warn('Pusher not connected. Cannot broadcast message.')
      return
    }

    try {
      // Get the conversation channel
      const channel = this.channels.get(conversationId)
      if (channel) {
        // Use client event (limited to current connection)
        channel.trigger('client-newMessage', {
          message,
          conversationId,
          timestamp: new Date().toISOString(),
        })
      } else {
        console.warn(`Not subscribed to conversation ${conversationId}`)
      }
    } catch (error) {
      console.error(`Failed to broadcast message: ${error.message}`)
    }
  }

  // Emit event to listeners
  emit(event, data) {
    const eventListeners = this.listeners.get(event)
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error)
        }
      })
    }
  }

  // Add event listener
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, [])
    }
    this.listeners.get(event).push(callback)
  }

  // Remove event listener
  off(event, callback) {
    const eventListeners = this.listeners.get(event)
    if (eventListeners) {
      const index = eventListeners.indexOf(callback)
      if (index > -1) {
        eventListeners.splice(index, 1)
      }
    }
  }

  // Get current user ID
  getCurrentUserId() {
    // Try to get from stored user ID (set when connecting)
    if (this.currentUserId) {
      return this.currentUserId
    }

    // Fallback to localStorage/sessionStorage
    return localStorage.getItem('currentUserId') || sessionStorage.getItem('currentUserId')
  }

  // Check if browser tab is currently active/visible
  isTabActive() {
    return !document.hidden
  }

  // Check if notification sound should be played
  shouldPlayNotificationSound(isFromCurrentUser) {
    // Don't play sound if message is from current user
    if (isFromCurrentUser) {
      return false
    }

    // Only play sound if tab is inactive (hidden)
    return !this.isTabActive()
  }

  // Setup tab visibility tracking
  setupTabVisibilityTracking() {
    // Listen for visibility changes
    document.addEventListener('visibilitychange', () => {
      const isVisible = !document.hidden

      // Notify listeners
      this.tabVisibilityChangeListeners.forEach((callback) => {
        try {
          callback(isVisible)
        } catch (error) {
          console.error('Error in tab visibility change listener:', error)
        }
      })
    })
  }

  // Add tab visibility change listener
  onTabVisibilityChange(callback) {
    this.tabVisibilityChangeListeners.push(callback)
  }

  // Remove tab visibility change listener
  offTabVisibilityChange(callback) {
    const index = this.tabVisibilityChangeListeners.indexOf(callback)
    if (index > -1) {
      this.tabVisibilityChangeListeners.splice(index, 1)
    }
  }

  // Check if browser tab is currently active/visible
  isTabActive() {
    return !document.hidden
  }

  // Check if notification sound should be played
  shouldPlayNotificationSound(isFromCurrentUser) {
    // Don't play sound if disabled
    if (!this.soundSettings.enabled) {
      return false;
    }

    // Don't play sound if message is from current user
    if (isFromCurrentUser) {
      return false
    }

    // Check tab visibility based on settings
    const isTabActive = this.isTabActive();
    if (this.soundSettings.playWhenTabActive) {
      // Play sound regardless of tab status
      return true;
    } else {
      // Only play sound if tab is inactive (hidden) - default behavior
      return !isTabActive;
    }
  }

  // Setup tab visibility tracking
  setupTabVisibilityTracking() {
    // Listen for visibility changes
    document.addEventListener('visibilitychange', () => {
      const isVisible = !document.hidden

      // Notify listeners
      this.tabVisibilityChangeListeners.forEach((callback) => {
        try {
          callback(isVisible)
        } catch (error) {
          console.error('Error in tab visibility change listener:', error)
        }
      })
    })
  }

  // Add tab visibility change listener
  onTabVisibilityChange(callback) {
    this.tabVisibilityChangeListeners.push(callback)
  }

  // Remove tab visibility change listener
  offTabVisibilityChange(callback) {
    const index = this.tabVisibilityChangeListeners.indexOf(callback)
    if (index > -1) {
      this.tabVisibilityChangeListeners.splice(index, 1)
    }
  }

  // Set current user ID (called when connecting)
  setCurrentUserId(userId) {
    this.currentUserId = userId
  }

  // Handle connection errors
  handleConnectionError(error) {
    console.error('Pusher connection error:', error)
    this.isConnected = false
    toast.error(
      'Failed to connect to chat server. Please check your connection and refresh the page.'
    )
  }

  // Disconnect from Pusher
  disconnect() {
    if (this.pusher) {
      // Unsubscribe from all channels
      this.channels.forEach((channel, conversationId) => {
        this.pusher.unsubscribe(`conversation.${conversationId}`)
      })
      this.channels.clear()

      this.pusher.disconnect()
      this.pusher = null
      this.isConnected = false
      console.log('Disconnected from Pusher/Soketi')
    }
  }

  // Get connection status
  getConnectionStatus() {
    const status = {
      isConnected: this.isConnected,
      channelsCount: this.channels.size,
      channels: Array.from(this.channels.keys()),
      pusherState: this.pusher?.connection?.state || 'unknown',
      currentUserId: this.currentUserId,
      notificationSoundLoaded: this.notificationSound
        ? this.notificationSound.readyState >= 2
        : false,
      tabVisibility: {
        isActive: this.isTabActive(),
        isHidden: document.hidden,
        visibilityState: document.visibilityState,
      },
    }
    // please make it remain commented
    // console.log('🔍 Connection status:', status);
    return status
  }

  // Test notification sound (for debugging)
  testNotificationSound() {
    this.playNotificationSound()
  }

  // Force enable audio (for debugging)
  forceEnableAudio() {
    if (this.notificationSound) {
      this.notificationSound
        .play()
        .then(() => {

        })
        .catch((error) => {
          console.warn('Failed to force enable audio:', error)
        })
    }
  }

  // Sound settings control methods
  setSoundEnabled(enabled) {
    this.soundSettings.enabled = enabled;
  }

  setPlayWhenTabActive(playWhenActive) {
    this.soundSettings.playWhenTabActive = playWhenActive;
  }

  setSoundVolume(volume) {
    this.soundSettings.volume = Math.max(0, Math.min(1, volume)); // Clamp between 0 and 1
    if (this.notificationSound) {
      this.notificationSound.volume = this.soundSettings.volume;
    }
  }

  getSoundSettings() {
    return { ...this.soundSettings };
  }

  // Test sound with current settings
  testSoundWithSettings() {
    this.playNotificationSound();
  }
}

// Create singleton instance
const chatWebSocketService = new ChatWebSocketService();

// Add global console helpers for easy testing
if (typeof window !== 'undefined') {
  window.chatSound = {
    // Quick access to sound controls
    enable: () => chatWebSocketService.setSoundEnabled(true),
    disable: () => chatWebSocketService.setSoundEnabled(false),
    playAlways: () => chatWebSocketService.setPlayWhenTabActive(true),
    playWhenInactive: () => chatWebSocketService.setPlayWhenTabActive(false),
    setVolume: (vol) => chatWebSocketService.setSoundVolume(vol),
    test: () => chatWebSocketService.testNotificationSound(),
    testWithSettings: () => chatWebSocketService.testSoundWithSettings(),
    settings: () => console.log('Current sound settings:', chatWebSocketService.getSoundSettings()),
    status: () => console.log('Connection status:', chatWebSocketService.getConnectionStatus()),

    // Quick presets
    preset: {
      quiet: () => {
        chatWebSocketService.setSoundEnabled(true);
        chatWebSocketService.setPlayWhenTabActive(false);
        chatWebSocketService.setSoundVolume(0.1);
      },
      normal: () => {
        chatWebSocketService.setSoundEnabled(true);
        chatWebSocketService.setPlayWhenTabActive(false);
        chatWebSocketService.setSoundVolume(0.3);
      },
      loud: () => {
        chatWebSocketService.setSoundEnabled(true);
        chatWebSocketService.setPlayWhenTabActive(true);
        chatWebSocketService.setSoundVolume(0.7);
      },
      off: () => {
        chatWebSocketService.setSoundEnabled(false);
      }
    }
  };
}

export default chatWebSocketService
